// Tiered fabricability assessment for a stator configuration. Pure module —
// no geometry imports. Rules encode what the DRC pipeline has actually
// verified (spec 2026-07-03, amended): the verified envelope is 3 phases,
// 1..4 coils/phase (wye; delta requires even k), with solder terminals;
// everything else carries an explicit, human-readable reason. The UI shows
// the worst tier; downloads never block.

export type SupportLevel = 'verified' | 'unverified' | 'broken';
export interface SupportReason { level: 'unverified' | 'broken'; text: string; }
export interface SupportAssessment { level: SupportLevel; reasons: SupportReason[]; }

export interface SupportInput {
  numCoils: number;
  numPhases: number;
  numPoles: number;
  windingType: 'wye' | 'delta' | 'independent';
  terminalStyle: 'solder' | 'wire' | 'screw';
  coilShape: 'spiral' | 'wedge';
  deltaBusDropped: boolean;
  innerJoinsDropped: boolean;
  outerJoinsDropped: boolean;
  hookupsDropped: boolean;
  rotatingFieldOk: boolean;
  numLayers: number;
  /** 'adjacent' (default) | 'stacking-tabs' — only meaningful with
   *  windingType === 'independent'; ignored otherwise (mirrors how the
   *  component's `stackingTabsActive` getter gates on windingType too). */
  terminalLayout: 'adjacent' | 'stacking-tabs';
  /** `assessEarLayout(...).valid` — false means the six ears collide at this
   *  pole/coil combination and were NOT emitted (honest plain-circle
   *  fallback). Only consulted when terminalLayout === 'stacking-tabs'. */
  earLayoutValid: boolean;
  /** `assessEarLayout(...).reasons` — human-readable collision detail, folded
   *  into the broken-tier message so the banner names the actual azimuths. */
  earLayoutReasons: string[];
}

export function assessConfigSupport(input: SupportInput): SupportAssessment {
  const { numCoils, numPhases, numPoles, windingType, terminalStyle, coilShape, deltaBusDropped, innerJoinsDropped, outerJoinsDropped, hookupsDropped, rotatingFieldOk, numLayers, terminalLayout, earLayoutValid, earLayoutReasons } = input;
  const k = Math.floor(numCoils / numPhases);
  const reasons: SupportReason[] = [];

  if (!rotatingFieldOk) {
    reasons.push({ level: 'broken', text:
      'This coil/pole pairing cannot produce a rotating field — the phase EMFs are not electrically separated, so the motor will not turn. See the recommended pole counts.' });
  }
  if (windingType === 'delta' && k % 2 === 1) {
    reasons.push({ level: 'broken', text:
      'Delta needs an even number of coils per phase — no delta bus is generated.' });
  }
  if (windingType === 'delta' && numPhases > 3) {
    reasons.push({ level: 'broken', text:
      'Delta with more than 3 phases stacks two phase nets on one copper layer (short).' });
  }
  // Task-5 audit: deltaBusDropped's text is delta-specific ("the delta bus"), but
  // the flag itself is a plain windingType-agnostic boolean in SupportInput — gate
  // on windingType so a caller can never surface delta language for wye/independent
  // (the flag can only be legitimately true for delta in practice, since only
  // computeBusRouting's 'delta' branch ever sets it, but SupportInput does not
  // enforce that itself).
  if (windingType === 'delta' && deltaBusDropped) {
    reasons.push({ level: 'broken', text:
      'The outer margin is too tight for the delta bus at this terminal style — the bus was NOT generated (open circuit).' });
  }
  if (innerJoinsDropped) {
    reasons.push({ level: 'broken', text:
      'The inner margin cannot fit this many series joins — some coil connections were NOT generated (open circuit). Widen the inner margin or reduce coils per phase.' });
  }
  if (outerJoinsDropped) {
    // Task-5 review fix: the SAME outer-margin pool (one (layer,slot) pair per
    // NET) allocates wye's neutral tree, delta's cycle nets, AND independent's
    // per-phase return-lead tails — this flag already honestly fires for
    // independent when k/margin can't fit every phase's tail (verified live:
    // NUM_COILS=12 WINDING_TYPE=independent at the default 5mm margin drops
    // with this flag; at 7mm it fits clean, matching the DRC). No separate
    // static k-threshold is needed for independent — this dynamic, geometry-
    // derived signal already carries the k-signal exactly like it does for
    // wye/delta. Wording is windingType-specific since independent has no
    // shared "bus", only per-phase tails.
    reasons.push({ level: 'broken', text: windingType === 'independent'
      ? 'The outer margin cannot fit this many phases\' return-lead tails — some connections were NOT generated (open circuit). Widen the outer margin. (6+ layer boards also reserve an outer slot for the layer-hookup ring.)'
      : 'The outer margin cannot fit the bus for this many coils per phase — some connections were NOT generated (open circuit). Widen the outer margin. (6+ layer boards also reserve an outer slot for the layer-hookup ring.)' });
  }
  if (hookupsDropped) {
    reasons.push({ level: 'broken', text:
      'The outer margin cannot fit the N-layer hookup ring — some layer connections were NOT generated (open circuit). Widen the outer margin (≥7 mm fits the default board).' });
  }
  // Independent (6-lead) mode: gated by DRC only at the default 4-layer
  // envelope (see task-3-report.md — 4L spiral/wedge independent PASS at
  // defaults). Unlike wye/delta, 6-layer independent has NOT been cleared: a
  // probe (`NUM_LAYERS=6 OUTER_MARGIN=7 WINDING_TYPE=independent`) found a
  // real drill-keepaway finding — 8-layer independent is already covered by
  // the generic 8-layer reason below, so this only needs to name 6L
  // specifically (narrowed in review: firing on "numLayers !== 4" doubled up
  // with that generic reason at 8L, stacking two yellows for the same fact).
  // 2-layer is not reachable from the UI (setNumLayers only accepts 4/6/8,
  // the even-N campaign's floor) so it is deliberately not handled here.
  //
  // There is NO static k-threshold for independent (review fix, replacing an
  // earlier `k >= 4` rule that was factually wrong — `OUTER_MARGIN=7
  // NUM_COILS=12 WINDING_TYPE=independent` (k=4) DRC-PASSES clean): like
  // wye/delta, independent's k-signal is carried entirely by the dynamic
  // `outerJoinsDropped`/`hookupsDropped` flags above, which are computed from
  // the SAME outer-margin pool independent's per-phase return-tail nets
  // allocate from — confirmed live (see the outerJoinsDropped comment above)
  // that a too-tight k/margin combo for independent honestly drops via that
  // flag, and a config that fits (om7 k=4) carries no reason at all. This
  // rule never fires for wye/delta (grepped: the two `windingType ===
  // 'delta'` checks and the one `windingType === 'wye'` check are the only
  // other windingType comparisons here). It is no longer the only
  // windingType==='independent'-only rule in this function — the
  // stacking-tabs block below (Task 5/6) adds two more, both also gated on
  // `windingType === 'independent'`.
  if (windingType === 'independent' && numLayers === 6) {
    reasons.push({ level: 'unverified', text:
      'Independent (6-lead) winding is only DRC-verified on the default 4-layer stack — a 6-layer probe found a drill-keepaway finding.' });
  }
  // Stacking-tabs (Task 5): the honest-drop surfacing reuses assessEarLayout's
  // OWN dynamic collision flag — not an invented static pole/coil cap — for the
  // broken tier (a colliding pole/coil combination is a real geometric fact,
  // computed live from the params, exactly the "dynamic flags, no static caps"
  // house rule the independent-mode k-threshold review fix established). The
  // DRC matrix has raster-checked exactly two tuples (4L/8P/6C/3-phase/solder,
  // spiral and wedge — README rows 15/16); every other collision-free
  // combination is unverified, named explicitly so the banner reads like the
  // encoder tab's fixture-divergence notice rather than an opaque yellow.
  if (windingType === 'independent' && terminalLayout === 'stacking-tabs') {
    if (!earLayoutValid) {
      reasons.push({ level: 'broken', text:
        `Stacking-tabs ears collide at this pole/coil combination — ears were NOT generated (falls back to the adjacent-pads outline, no external stacking terminals). ${earLayoutReasons.join(' ')}`.trim() });
    } else if (numPoles !== 8 || numCoils !== 6 || numPhases !== 3 || numLayers !== 4 || terminalStyle !== 'solder') {
      reasons.push({ level: 'unverified', text:
        'Stacking-tabs is only DRC-verified at the default 8-pole / 6-coil / 3-phase / 4-layer / solder-terminal combination (spiral or wedge coil shape) — this configuration has not been through the DRC.' });
    }
  }
  if (numLayers === 8) {
    reasons.push({ level: 'unverified', text:
      '8-layer routing is not DRC-verified.' });
  }
  if (numLayers === 6 && coilShape === 'wedge') {
    reasons.push({ level: 'unverified', text:
      '6-layer wedge is not DRC-verified — known inner-ring crowding at the chaining rungs.' });
  }
  if (k >= 5) {
    reasons.push({ level: 'unverified', text:
      '5+ coils per phase is not DRC-verified.' });
  }
  if (numPhases > 3 && windingType === 'wye') {
    reasons.push({ level: 'unverified', text:
      'More than 3 phases is not DRC-verified.' });
  }
  if (terminalStyle !== 'solder') {
    reasons.push({ level: 'unverified', text:
      'Wire/screw terminals are only DRC-verified with widened margins.' });
  }

  const level: SupportLevel = reasons.some(r => r.level === 'broken') ? 'broken'
    : reasons.length > 0 ? 'unverified'
    : 'verified';
  return { level, reasons };
}

// ── Encoder tab (Task 9) ─────────────────────────────────────────────────────
// The encoder ring/target boards have a much narrower verified envelope than
// the stator: exactly ONE parameter tuple has been through the raster DRC
// (see pcb-stator-encoder-board.spec.ts / tools/pcb-stator-drc/generate.ts).
// Unlike the stator's mostly-green matrix, this tier is honest that the
// feature is young — a permanent informational yellow reminds the user the
// margins are thin even on the verified tuple.

export interface EncoderSupportInput {
  bandsValid: boolean;             // computeEncoderBands(...).valid
  bandsReasons: string[];          // computeEncoderBands(...).reasons
  mountingHoles: number;           // stator's mountingHoles count (shared bolt circle)
  boardComputed: boolean;          // true once buildEncoderBoard has been solved for the current params
  boardValid: boolean;             // buildEncoderBoard(...).valid; ignored while !boardComputed
  boardReasons: string[];          // buildEncoderBoard(...).reasons
  keyFingerDropped: boolean;       // RotorLayout.keyFingerDropped
  recessDropped: boolean;          // RotorLayout.recessDropped — recess requested but could not fit; rotor prints solid
  /** Names of every input diverging from VERIFIED_ENCODER_FIXTURE (the exact
   *  tuple the raster DRC checked — full fingerprint: encoder params + hub
   *  radius + the stator/rotor/mounting inputs the boards derive from).
   *  Empty = verified. */
  divergingParams: string[];
}

export function assessEncoderSupport(input: EncoderSupportInput): SupportAssessment {
  const reasons: SupportReason[] = [];

  if (!input.bandsValid) {
    reasons.push({ level: 'broken', text:
      `Sensing geometry does not fit: ${input.bandsReasons.join(' ') || 'unknown reason'}` });
  }
  if (input.mountingHoles === 0) {
    reasons.push({ level: 'broken', text:
      'No mounting holes — enable stator mounting holes so the encoder ring shares the stator\'s bolt circle.' });
  }
  if (input.boardComputed && !input.boardValid) {
    reasons.push({ level: 'broken', text:
      `Encoder-ring routing did not converge: ${input.boardReasons.join(' ') || 'unknown reason'}` });
  }
  if (input.keyFingerDropped) {
    reasons.push({ level: 'broken', text:
      'The rotor key finger was dropped — the web between magnet pockets is too narrow to hold it, so the target ring can only be phased by careful glue-up, not a mechanical key.' });
  }
  if (input.recessDropped) {
    reasons.push({ level: 'broken', text:
      'The rotor recess was dropped — the target seat cannot fit this rotor (its inner wall would swallow the bore, or the seat is deeper than the disc), so the rotor prints SOLID and the target ring has nowhere to mount.' });
  }
  if (input.divergingParams.length > 0) {
    reasons.push({ level: 'unverified', text:
      `This encoder configuration is not DRC-verified — it diverges from the raster-checked fixture on: ${input.divergingParams.join(', ')}. Only that exact fixture has been raster-checked clean.` });
  }
  reasons.push({ level: 'unverified', text:
    'Encoder boards are newly DRC-verified — copper margins are thin (sub-pixel at the raster check\'s resolution).' });

  const level: SupportLevel = reasons.some(r => r.level === 'broken') ? 'broken' : 'unverified';
  return { level, reasons };
}
